import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useIsAdmin } from '../lib/admin';
import { Button } from './ui';

const logo = `${process.env.PUBLIC_URL}/logo.png`;

// DESIGN.md §8.2 — the nav is a lower-third bar: carbon-950 ground, a 3px
// accent spine under the active item, micro-label caps for the items, and the
// logo capped at 40px (it was 80px, which is why the header read unbalanced).
// Count badges are the only radius-full in the system (§4).
function NavItem({ item, active, onClick, className }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex items-center gap-space-3 whitespace-nowrap py-space-4 text-micro uppercase transition-[color] duration-fast ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
        active ? 'text-carbon-050' : 'text-carbon-300 hover:text-carbon-050'
      } ${className || ''}`}
    >
      {item.label}
      {item.count > 0 && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-radius-full bg-accent px-space-2 text-micro font-bold tabular-nums text-carbon-950">
          {item.count}
        </span>
      )}
      {/* The spine is the active marker — a value/color step on the leading
          edge rather than a weight change (§5). */}
      {active && (
        <span
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-[3px] bg-accent"
        />
      )}
    </button>
  );
}

export default function Nav({ page, onNavigate, user, onSignIn, board, recruitBoard }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { isAdmin } = useIsAdmin();
  const navItems = [
    { key: 'search', label: 'Players' },
    { key: 'portal', label: 'Transfer Portal' },
    { key: 'recruiting', label: 'Recruiting' },
    { key: 'board', label: 'Scout Board', count: board.length },
    { key: 'recruitboard', label: 'Recruit Board', count: recruitBoard.length },
    ...(user ? [{ key: 'season-predictor', label: 'Season Predictor' }] : []),
    { key: 'draft', label: 'Mock Draft' },
    // { key: 'mockdraftv2', label: 'Mock Draft V2' }, // V2 paused 2026-05-28 — see docs/MOCKDRAFT_V2_BUILD_SPEC.md
    { key: 'bigboard', label: 'Big Board' },
    ...(isAdmin ? [{ key: 'admin', label: 'Admin' }] : []),
  ];

  return (
    <header className="bg-carbon-950 px-space-8">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-space-6">
        <img
          src={logo}
          alt="Hail Mary Scout"
          className="h-10 w-auto cursor-pointer"
          onClick={() => onNavigate('home')}
        />
        {/* Desktop nav */}
        <nav className="hidden items-center gap-space-6 lg:flex">
          {navItems.map(item => (
            <NavItem
              key={item.key}
              item={item}
              active={page === item.key}
              onClick={() => onNavigate(item.key)}
            />
          ))}
          {user ? (
            <Button variant="ghost" onClick={() => supabase.auth.signOut()}>Sign Out</Button>
          ) : (
            <Button onClick={onSignIn}>Sign In</Button>
          )}
        </nav>
        {/* Mobile hamburger */}
        <button
          type="button"
          className="rounded-radius-sm py-space-4 text-carbon-300 transition-[color] duration-fast ease-standard hover:text-carbon-050 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent lg:hidden"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {menuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
        </button>
      </div>
      {/* Mobile menu */}
      {menuOpen && (
        <div className="flex flex-col gap-space-4 border-t border-carbon-800 py-space-5 lg:hidden">
          {navItems.map(item => (
            <NavItem
              key={item.key}
              item={item}
              active={page === item.key}
              onClick={() => { onNavigate(item.key); setMenuOpen(false); }}
              className="justify-start"
            />
          ))}
          {user ? (
            <Button variant="ghost" onClick={() => supabase.auth.signOut()} className="self-start">Sign Out</Button>
          ) : (
            <Button onClick={onSignIn} className="self-start">Sign In</Button>
          )}
        </div>
      )}
    </header>
  );
}
